import React, { useState, useEffect, useCallback } from "react";
import { Formik } from "formik";
import * as Yup from "yup";

import {
  uploadDocument,
  getDocuments,
  deleteDocument,
  getUnreadFileCounts,
  markFileAsRead,
  moveClientFileToClientArchive,
} from "../services/source";
import { openFileUrl } from "../services/fileUtils";
import auth from "../services/auth";

import {
  Paper,
  Typography,
  Tabs,
  Tab,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Alert,
} from "@mui/material";

import {
  UploadFile,
  Description,
  Visibility,
  Delete,
  Done,
  Archive,
} from "@mui/icons-material";

/* =========================
    VALIDATION
  ========================= */
const getDocumentSchema = (tabIndex) => {
  // Certidão is at index 0: only validade required
  if (tabIndex === 0) {
    return Yup.object({
      validade: Yup.string().required("Data de validade é obrigatória"),
      publicacao: Yup.string(),
    });
  }
  // Publicação (index 1), Estatuto (index 2), NIF (index 3), Alvara (index 4): only publicacao required
  if (tabIndex === 1 || tabIndex === 2 || tabIndex === 3 || tabIndex === 4) {
    return Yup.object({
      validade: Yup.string(),
      publicacao: Yup.string().required("Data de publicação é obrigatória"),
    });
  }
  // Documentos Sociais (index 5), Documentos Gerentes (index 6), and Procurações (index 7): only validade required
  if (tabIndex === 5 || tabIndex === 6 || tabIndex === 7) {
    return Yup.object({
      validade: Yup.string().required("Data de validade é obrigatória"),
      publicacao: Yup.string(),
    });
  }
  // For all other tabs: both required
  return Yup.object({
    validade: Yup.string().required("Data de validade é obrigatória"),
    publicacao: Yup.string().required("Data de publicação é obrigatória"),
  });
};

/* =========================
    CATEGORIES
  ========================= */
const documentCategories = [
  "Certidão",
  "Publicação",
  "Estatuto",
  "NIF",
  "Alvara",
  "Documentos dos Sócios",
  "Documentos dos Gerentes",
  "Procurações",
];

const Documents = ({ clientId, companyId: companyIdProp }) => {
  const currentUser = auth.getCurrentUser();
  const userId = currentUser?.data?.id;
  const userType = currentUser?.data?.type;
  const companyId =
    companyIdProp ||
    currentUser?.data?.companyId ||
    currentUser?.data?.id ||
    "";
  const [selectedDocTab, setSelectedDocTab] = useState(0);
  const [documents, setDocuments] = useState({});
  const [dbDocuments, setDbDocuments] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [unreadByCategory, setUnreadByCategory] = useState({});
  const [movingFileId, setMovingFileId] = useState(0);
  const [archiveError, setArchiveError] = useState("");
  const [archiveSuccess, setArchiveSuccess] = useState("");
  const FILE_BASE_URL = process.env.REACT_APP_API_URL;

  const refreshUnreadCount = useCallback(async () => {
    if (!clientId || !userId || !userType) {
      setUnreadByCategory({});
      return;
    }

    const res = await getUnreadFileCounts({
      user_id: userId,
      user_type: userType,
      client_id: clientId,
    });

    if (res?.success) {
      setUnreadByCategory(res?.data?.documentos_por_categoria || {});
    }
  }, [clientId, userId, userType]);

  const loadDocuments = useCallback(async () => {
    if (!clientId) return;

    const res = await getDocuments(clientId);

    if (res?.success) {
      setDbDocuments(res.data);
    }
  }, [clientId]);

  /* =========================
     LOAD DB DOCS UseEffect
  ========================= */
  useEffect(() => {
    if (!clientId) return;

    const load = async () => {
      const res = await getDocuments(clientId);
      if (res?.success) setDbDocuments(res.data);
    };

    load();
  }, [clientId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    refreshUnreadCount();
  }, [refreshUnreadCount]);

  /* =========================
     LOCAL UPLOAD
  ========================= */
  const handleDocumentUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const file = files[0];

    const fileEntry = {
      file,
      name: file.name,
      size: file.size,
      validade: "",
      publicacao: "",
      uploaded: false,
    };

    setDocuments((prev) => ({
      ...prev,
      [selectedDocTab]: [...(prev[selectedDocTab] || []), fileEntry],
    }));

    event.target.value = "";
  };

  /* =========================
     UPLOAD
  ========================= */
  const uploadFileToServer = async (fileEntry, category) => {
    const formData = new FormData();

    formData.append("file", fileEntry.file);
    formData.append("cliente_id", clientId);
    formData.append("company_id", companyId);
    formData.append("user_id", userId || "");
    formData.append("user_type", userType || "");
    formData.append("categoria", category);
    formData.append("data_publicacao", fileEntry.publicacao || "");
    formData.append("data_validade", fileEntry.validade || "");
    formData.append("observacoes", "");

    const res = await uploadDocument(formData);
    return res?.data?.success ? Number(res?.data?.id || 0) : 0;
  };

  /* =========================
     REMOVE LOCAL ONLY
  ========================= */
  const removeDocument = (index) => {
    setDocuments((prev) => ({
      ...prev,
      [selectedDocTab]: (prev[selectedDocTab] || []).filter(
        (_, i) => i !== index,
      ),
    }));
  };

  /* =========================
     FILTER DB BY TAB
  ========================= */
  const filteredDbDocs = dbDocuments.filter(
    (doc) => doc.categoria === documentCategories[selectedDocTab],
  );

  const localDocs = documents[selectedDocTab] || [];

  /* =========================
    DELETE DOCUMENT
  ========================= */
  const handleDeleteDocument = (file) => {
    setSelectedFile(file);
    setDeleteModalOpen(true);
  };

  const confirmDeleteDocument = async () => {
    if (!selectedFile) return;

    const res = await deleteDocument(selectedFile.id);
    const data = res?.data;

    if (data?.success) {
      await loadDocuments();
    } else {
      console.error(data);
      alert("Erro ao apagar documento");
    }

    setDeleteModalOpen(false);
    setSelectedFile(null);
  };

  const handleOpenDocument = async (file) => {
    try {
      const ok = await openFileUrl(
        file.caminho,
        file.nome_original || file.nome_ficheiro,
      );
      if (!ok) window.open(`${FILE_BASE_URL}/${file.caminho}`, "_blank");
    } catch (err) {
      window.open(`${FILE_BASE_URL}/${file.caminho}`, "_blank");
    }

    if (!userId || !userType || !file?.id) return;

    await markFileAsRead({
      user_id: userId,
      user_type: userType,
      file_type: "document",
      file_id: Number(file.id),
    });

    await refreshUnreadCount();
  };

  const handleMoveToArchive = async (file) => {
    const fileId = Number(file?.id || 0);
    if (!clientId || !companyId || !fileId) {
      setArchiveError("Não foi possível identificar o ficheiro selecionado.");
      return;
    }

    try {
      setMovingFileId(fileId);
      setArchiveError("");
      setArchiveSuccess("");

      const res = await moveClientFileToClientArchive({
        company_id: companyId,
        client_id: clientId,
        file_type: "documentos",
        file_id: fileId,
      });

      if (!res?.success) {
        throw new Error(
          res?.message || "Não foi possível mover o ficheiro para o arquivo.",
        );
      }

      await loadDocuments();
      await refreshUnreadCount();
      setArchiveSuccess("Ficheiro movido para o arquivo com sucesso.");
    } catch (error) {
      console.error("Move document to archive error:", error);
      setArchiveError(
        error?.message || "Não foi possível mover o ficheiro para o arquivo.",
      );
    } finally {
      setMovingFileId(0);
    }
  };

  /* ========================= */

  return (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
        Documentos
      </Typography>

      {/* TABS */}
      <Tabs
        value={selectedDocTab}
        onChange={(_, v) => setSelectedDocTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2,
          backgroundColor: "rgba(114, 4, 241, 0.18)",
          borderRadius: 2,
          "& .MuiTabs-indicator": { display: "none" },
        }}
      >
        {documentCategories.map((cat) => (
          <Tab
            key={cat}
            label={
              (unreadByCategory[cat] || 0) > 0 ? (
                <Badge
                  color="error"
                  badgeContent={unreadByCategory[cat] || 0}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  sx={{
                    "& .MuiBadge-badge": {
                      minWidth: 14,
                      height: 14,
                      fontSize: "0.65rem",
                      px: 0.5,
                      top: 2,
                      right: -8,
                    },
                  }}
                >
                  <span>{cat}</span>
                </Badge>
              ) : (
                cat
              )
            }
            sx={{
              textTransform: "none",
              "&.Mui-selected": {
                color: "#fff",
                backgroundColor: "#9c27b0",
              },
            }}
          />
        ))}
      </Tabs>

      {/* UPLOAD BUTTON */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" component="label" color="secondary">
          <UploadFile sx={{ mr: 1 }} />
          Carregar
          <input hidden type="file" onChange={handleDocumentUpload} />
        </Button>
      </Box>

      {archiveError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {archiveError}
        </Alert>
      ) : null}

      {archiveSuccess ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {archiveSuccess}
        </Alert>
      ) : null}

      {/* LIST */}
      <List dense>
        {/* ================= DB DOCUMENTS ================= */}
        {filteredDbDocs.map((file) => (
          <ListItem key={file.id} divider sx={{ display: "block" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Description sx={{ mr: 1, color: "green" }} />

              <ListItemText
                primary={file.nome_original}
                secondary={`${Math.round(file.tamanho / 1024)} KB`}
              />

              <ListItemSecondaryAction>
                <IconButton
                  color="warning"
                  title="Visualizar documento"
                  onClick={() => handleOpenDocument(file)}
                >
                  <Visibility />
                </IconButton>
                <IconButton
                  color="secondary"
                  title="Mover para Arquivo"
                  onClick={() => handleMoveToArchive(file)}
                  disabled={movingFileId === Number(file.id)}
                >
                  <Archive />
                </IconButton>
                <IconButton
                  title="Eliminar documento"
                  color="error"
                  onClick={() => handleDeleteDocument(file)}
                >
                  <Delete />
                </IconButton>
              </ListItemSecondaryAction>
            </Box>

            {/* SHOW DB INFO (READ ONLY) */}
            <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
              {(selectedDocTab === 0 ||
                selectedDocTab === 4 ||
                selectedDocTab === 5 ||
                selectedDocTab === 6 ||
                selectedDocTab === 7) && (
                <TextField
                  size="small"
                  value={file.data_validade || ""}
                  disabled
                  label="Validade"
                />
              )}

              {selectedDocTab !== 5 &&
                selectedDocTab !== 6 &&
                selectedDocTab !== 7 && (
                  <TextField
                    size="small"
                    value={file.data_publicacao || ""}
                    disabled
                    label="Publicação"
                  />
                )}
            </Box>
          </ListItem>
        ))}

        {/* ================= LOCAL DOCUMENTS ================= */}
        {localDocs.map((file, index) => (
          <Formik
            key={`${file.name}-${index}`}
            initialValues={{
              validade: file.validade || "",
              publicacao: file.publicacao || "",
            }}
            validationSchema={getDocumentSchema(selectedDocTab)}
            enableReinitialize
            onSubmit={async (values) => {
              const payload = {
                ...file,
                ...values,
              };

              const uploadedId = await uploadFileToServer(
                payload,
                documentCategories[selectedDocTab],
              );

              if (uploadedId) {
                // ✅ REMOVE LOCAL ENTRY AFTER UPLOAD
                setDocuments((prev) => ({
                  ...prev,
                  [selectedDocTab]: (prev[selectedDocTab] || []).filter(
                    (_, i) => i !== index,
                  ),
                }));

                // ✅ REFRESH DB
                await loadDocuments();
                await refreshUnreadCount();
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleSubmit,
              isValid,
              dirty,
            }) => (
              <ListItem divider sx={{ display: "block" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Description sx={{ mr: 1 }} />

                  <ListItemText
                    primary={file.name}
                    secondary={`${Math.round(file.size / 1024)} KB`}
                  />

                  <ListItemSecondaryAction>
                    <IconButton
                      color="success"
                      onClick={handleSubmit}
                      disabled={!isValid}
                    >
                      <Done />
                    </IconButton>

                    <IconButton
                      color="error"
                      onClick={() => removeDocument(index)}
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </Box>

                {/* DATE FIELDS - CONDITIONAL RENDERING */}
                <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                  {(selectedDocTab === 0 ||
                    selectedDocTab === 4 ||
                    selectedDocTab === 5 ||
                    selectedDocTab === 6 ||
                    selectedDocTab === 7) && (
                    <TextField
                      label="Data validade"
                      name="validade"
                      type="date"
                      value={values.validade}
                      onChange={handleChange}
                      error={touched.validade && Boolean(errors.validade)}
                      helperText={touched.validade && errors.validade}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}

                  {selectedDocTab !== 5 &&
                    selectedDocTab !== 6 &&
                    selectedDocTab !== 7 && (
                      <TextField
                        label="Data publicação"
                        name="publicacao"
                        type="date"
                        value={values.publicacao}
                        onChange={handleChange}
                        error={touched.publicacao && Boolean(errors.publicacao)}
                        helperText={touched.publicacao && errors.publicacao}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                </Box>

                {!dirty && file.uploaded && (
                  <Typography variant="caption" color="success.main">
                    Documento enviado com sucesso
                  </Typography>
                )}
              </ListItem>
            )}
          </Formik>
        ))}
      </List>

      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogTitle>Eliminar documento</DialogTitle>

        <DialogContent>
          Tens a certeza que queres permanentemente eliminar este documento?
          <br />
          <b>{selectedFile?.nome_original || selectedFile?.name}</b>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)}>Cancelar</Button>

          <Button
            color="error"
            variant="contained"
            onClick={confirmDeleteDocument}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Documents;
